// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useState } from 'react';

import type { ProjectId } from '@evolvemep/api-ts';
import { Button, Flex, Stack, Text } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AutodeskAuthWrapper } from 'components/Autodesk/auth/AutodeskAuthWrapper';
import { useAutodeskAuth } from 'components/Autodesk/auth/useAutodeskAuth';
import { WrappedModal } from 'components/Mantine/Modals/WrappedModal';
import { AutodeskCloudBrowser } from 'modules/Shop/AutodeskCloudBrowser/AutodeskCloudBrowser';
import { AutodeskModelList } from 'modules/Shop/ModelBrowser/AutodeskModelList';

type Props = {
  projectId: ProjectId;
  refreshProjects: () => void;
};

export const LinkModelsPane = ({ projectId, refreshProjects }: Props) => {
  const { getAccessTokenAsync } = useAutodeskAuth();
  const [modalOpened, setModalOpened] = useState(false);

  useEffect(() => {
    void getAccessTokenAsync();
  }, [getAccessTokenAsync]);

  return (
    <>
      <Flex justify="space-between" align="center" mb="md">
        <Text fw={600}>Linked Models</Text>
        <AutodeskAuthWrapper>
          <Button variant="light" onClick={() => setModalOpened(true)} leftSection={<EvolveIcon icon="Link" />}>
            Link models
          </Button>
        </AutodeskAuthWrapper>
      </Flex>
      <Flex direction="column" flex="1 1 auto" style={{ overflowY: 'auto' }}>
        {!modalOpened && <AutodeskModelList projectId={projectId} allowUnlinking refreshProjects={refreshProjects} />}
      </Flex>
      <WrappedModal
        title="Link models"
        subtitle="A model can only be linked to one Shop Project at a time"
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        hideModalButtons
      >
        <Stack h={500} gap={0}>
          <AutodeskCloudBrowser projectId={projectId} refreshProjects={refreshProjects} />
        </Stack>
      </WrappedModal>
    </>
  );
};
